import { useState } from "react";

import { BsMetaUtil, LoopscalePointsAction, useOraclePrices } from "@bridgesplit/abf-react";
import { Column, Row, StatColumn, StatProps, Text, TextButton, TextVariant } from "@bridgesplit/ui";
import { PERCENT_DECIMALS, formatDate, formatPercent } from "@bridgesplit/utils";
import { SwapHorizOutlined } from "@mui/icons-material";
import { TransactionTypes } from "app/types";
import { useLoopscalePointStat } from "app/components/points/";
import { OracleLogos } from "app/components/oracles";
import { COPY } from "app/constants";

import { useMarketBorrowContext } from "./MarketBorrowContext";
import { useMarketContext } from "../common";
import { useScheduleDetails } from "./util";
import BorrowHealthStats from "./BorrowHealthStats";
import { useTransactionFeeStat } from "../../transactions";

const VARIANT: TextVariant = "body2";
export default function BorrowStats() {
    const scheduleDetails = useScheduleDetails();

    const { token } = useMarketContext();
    const {
        bestQuote,
        form: { collateralAmount, amount }
    } = useMarketBorrowContext();
    const { getOracle } = useOraclePrices([token?.mint]);

    const [showInterest, setShowInterest] = useState(false);

    const transactionFeeStat = useTransactionFeeStat({
        transactionType: TransactionTypes.MarketBorrow,
        variant: VARIANT
    });

    const usdPrice = getOracle(token?.mint)?.getUsdAmount(amount, token?.decimals);

    const pointsStat = useLoopscalePointStat({
        action: LoopscalePointsAction.Borrow,
        totalPrincipalAmountUsd: usdPrice,
        identifier: token?.mint
    });

    if (!scheduleDetails || !token || !amount || !collateralAmount) return null;

    const { loanEndDate, totalInterest } = scheduleDetails;

    const stats: StatProps[] = [
        pointsStat,
        {
            value: (
                <Row spacing={1}>
                    <Text variant={VARIANT}>
                        {showInterest
                            ? BsMetaUtil.formatAmount(token, totalInterest)
                            : formatPercent(bestQuote?.apy, { customDecimals: PERCENT_DECIMALS })}
                    </Text>
                    <TextButton variant={VARIANT} color="disabled" onClick={() => setShowInterest(!showInterest)}>
                        <SwapHorizOutlined />
                    </TextButton>
                </Row>
            ),
            caption: showInterest ? "Interest" : "APY",
            tooltip: showInterest
                ? `Total interest if repaid ${formatDate(loanEndDate, "relative")}`
                : "Your fixed borrowing rate for the entire loan duration"
        },
        {
            value: formatDate(loanEndDate),
            caption: "Rate fixed until"
        },
        transactionFeeStat
    ];

    return (
        <Column spacing={0.5}>
            <StatColumn captionVariant={VARIANT} variant={VARIANT} stats={stats} />
            <BorrowHealthStats variant={VARIANT} />
            <OracleStat />
        </Column>
    );
}

function OracleStat() {
    const { principalMint } = useMarketContext();
    const { form, bestQuote } = useMarketBorrowContext();
    if (!principalMint || !form.collateralMint) return null;

    const stat = [
        {
            caption: COPY.ORACLE_TERM_PLURAL,
            value: (
                <OracleLogos
                    principalMint={principalMint}
                    collateralMint={form.collateralMint}
                    marketInformation={bestQuote?.marketInformation}
                    variant={VARIANT}
                />
            )
        }
    ];

    return (
        <Column spacing={0.5}>
            <StatColumn captionVariant={VARIANT} variant={VARIANT} stats={stat} />
        </Column>
    );
}
